'use client';

import { trackAnalyticsCustomEvent } from '@ifixit/analytics';
import { cn } from '@ifixit/ui/lib/utils';
import type { MouseEvent, MouseEventHandler } from 'react';

export interface RibbonBannerData {
   title: string;
   displayText: string;
}

export interface RibbonBannerProps {
   ribbon: RibbonBannerData | null;
   page: string;
   onClick?: MouseEventHandler;
   className?: string;
}

// Stretches the banner's only anchor over the whole banner (see #59030).
// The section is the positioned ancestor, so the pseudo-element covers it.
const STRETCHED_LINK_CLASSES =
   "[&_a]:before:content-[''] [&_a]:before:cursor-inherit [&_a]:before:block [&_a]:before:absolute [&_a]:before:inset-0 [&_a]:before:z-0";

export function countAnchorTags(html: string): number {
   return (html.match(/<a[\s>]/gi) ?? []).length;
}

export function RibbonBanner({ ribbon, page, onClick, className }: RibbonBannerProps) {
   if (!ribbon) {
      return null;
   }

   const hasSingleLink = countAnchorTags(ribbon.displayText) === 1;

   return (
      <section
         className={cn(
            // FIXME: text-base is a temp PHP/NextJS base-font sync
            'flex items-center justify-center bg-brand-500 p-4 text-center text-base text-white min-h-[52px]',
            hasSingleLink && 'relative',
            className
         )}
      >
         <div
            // tighten multi-line text; links inherit white + underline, never wrap
            className={cn(
               'leading-tight [&_a]:whitespace-nowrap [&_a]:text-inherit [&_a]:underline [&_p]:my-0 [&_strong]:text-inherit',
               hasSingleLink && STRETCHED_LINK_CLASSES
            )}
            onClick={event => {
               trackRibbonBannerLinkClick(ribbon, page, event);
               onClick?.(event);
            }}
            dangerouslySetInnerHTML={{ __html: ribbon.displayText }}
         />
      </section>
   );
}

function trackRibbonBannerLinkClick(
   ribbon: RibbonBannerData,
   page: string,
   event: MouseEvent
): void {
   const element = event.target;
   if (element instanceof HTMLAnchorElement && element.href) {
      trackAnalyticsCustomEvent({
         eventCategory: 'Ribbon Banner',
         eventAction: `${ribbon.title} - Link - Clicked`,
         eventName: page,
      });
   }
}
